<template>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Date Range:</div>
				<div class="value">{{ formatDate(query.date_range, 'ddd, MMM D, YYYY') }}</div>
			</div>
			<div class="display-value">
				<div class="label">Vertical:</div>
				<div class="value">{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Date Range:</label>
							<div class="controls">
								<div class="field">
									<date-range-picker v-model="form.query.date_range" :date-options="date_picker_options" />
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Vertical:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.vertical_id"
										:options="$root.appStore.verticalOptions"
										option-label="label"
										option-value="value"
										placeholder="Filter by Vertical"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<gutter size="10px" />
	<div class="card padded table-card">
		<div class="data-table">
			<p-data-table
				:value="report_data"
				ref="sales_performance_table"
				data-key="team_id"
				class="sales-performance-table"
				scrollable
				scrollHeight="calc(100vh - 390px)"
				:loading="loading"
				v-model:expandedRows="expanded_teams"
				@rowExpand="handleTeamExpand"
				@rowCollapse="handleTeamCollapse"
			>
				<template #empty>
					<div class="dim">No Data</div>
				</template>
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<p-column expander style="width: 1em" />
				<p-column header="Team">
					<template #body="row">
						<strong style="font-size: 1.125em">{{ row.data.team_name }}</strong
						><br />
						<div class="sub-value">Marketshare: {{ percentage(row.data.spend / sumBy(report_data, 'spend')) }}</div>
					</template>
				</p-column>
				<p-column header="Spend" class="column-align-right">
					<template #body="row">
						{{ currency(row.data.spend, 0) }}<br />
						<div class="sub-value" v-html="trend(row.data.spend, row.data.compare_spend)" />
					</template>
				</p-column>
				<p-column header="Purchases" class="column-align-right">
					<template #body="row">
						{{ numberFormat(row.data.purchases) }}<br />
						<div class="sub-value" v-html="trend(row.data.purchases, row.data.compare_purchases)" />
					</template>
				</p-column>
				<p-column header="RPL" class="column-align-right">
					<template #body="row">
						{{ currency(row.data.rpl) }}<br />
						<div class="sub-value" v-html="trend(row.data.rpl, row.data.compare_rpl)" />
					</template>
				</p-column>
				<p-column header="Active Accounts" class="column-align-right">
					<template #body="row">
						{{ numberFormat(row.data.active_accounts) }}<br />
						<div class="sub-value" v-html="trend(row.data.active_accounts, row.data.compare_active_accounts)" />
					</template>
				</p-column>
				<p-column header="Spend/Account" class="column-align-right">
					<template #body="row">
						{{ currency(row.data.spa) }}<br />
						<div class="sub-value" v-html="trend(row.data.spa, row.data.compare_spa)" />
					</template>
				</p-column>
				<template #expansion="teamData">
					<template v-if="teamData.data.team_id === 'ltas'">
						<user-sub-table :teamId="teamData.data.team_id" :user-data="teamData.data.user_data" />
					</template>
					<template v-else>
						<product-sub-table
							:teamId="teamData.data.team_id"
							:product-data="teamData.data.product_data"
							:expanded-rows="expanded_products[teamData.data.team_id]"
						/>
					</template>
				</template>
			</p-data-table>
		</div>
	</div>
</template>

<script>
import pCalendar from 'primevue/calendar';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import lineLoader from '@/components/elements/LineLoader.vue';
import radioGroup from '@/components/forms/RadioGroup.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import quickFilter from '@/components/widgets/QuickFilter.vue';
import { currency, displayLabel, displaySelected, formatDate, numberFormat, percentage, trend } from '@/lib/Filters';
import { reportSalesPerformance } from '@GQL';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { cloneDeep, isEqual, sumBy } from 'lodash-es';
import productSubTable from './ProductSubTable.vue';
import userSubTable from './UserSubTable.vue';

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

export default {
	name: 'SalesPerformanceTable',
	components: {
		dateRangePicker,
		lineLoader,
		pCalendar,
		queryForm,
		quickFilter,
		radioGroup,
		productSubTable,
		userSubTable,
	},
	data() {
		return {
			loading: false,
			query: {
				date_range: [dayjs().startOf('isoWeek').toDate(), dayjs().toDate()],
				compare: null,
				filters: {
					vertical_id: [],
				},
			},
			report_data: [],
			expanded_teams: [],
			expanded_products: {
				account_managers: [],
			},
			date_picker_options: [
				{
					label: 'Today',
					icon: 'calendar-today',
					value: [dayjs().startOf('day').toDate(), dayjs().toDate()],
					compare: [dayjs().subtract(1, 'day').startOf('day').toDate(), dayjs().subtract(1, 'day').toDate()],
				},
				{
					label: 'Yesterday',
					icon: 'calendar-today',
					value: [dayjs().subtract(1, 'day').startOf('day').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
					compare: [
						dayjs().subtract(8, 'days').startOf('day').toDate(),
						dayjs().subtract(8, 'days').endOf('day').toDate(),
					],
				},
				{
					label: 'This Week (WTD)',
					icon: 'calendar-today',
					value: [dayjs().startOf('isoWeek').toDate(), dayjs().toDate()],
					compare: [dayjs().subtract(1, 'week').startOf('isoWeek').toDate(), dayjs().subtract(1, 'week').toDate()],
				},
				{
					label: 'This Month (MTD)',
					icon: 'calendar-today',
					value: [dayjs().startOf('month').toDate(), dayjs().toDate()],
					compare: [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').toDate()],
				},
				{
					label: 'Last Month',
					icon: 'calendar-today',
					value: [
						dayjs().subtract(1, 'month').startOf('month').toDate(),
						dayjs().subtract(1, 'month').endOf('month').toDate(),
					],
					compare: [
						dayjs().subtract(2, 'months').startOf('month').toDate(),
						dayjs().subtract(2, 'months').endOf('month').toDate(),
					],
				},
			],
		};
	},
	methods: {
		currency,
		displayLabel,
		displaySelected,
		formatDate,
		numberFormat,
		percentage,
		sumBy,
		trend,
		async getReportData(query, refresh) {
			const report_query = cloneDeep(query);
			report_query.compare = null;

			// Find the compare value
			const selected_option = this.date_picker_options.find((option) => {
				return isEqual(option.value, report_query.date_range);
			});
			if (selected_option) {
				report_query.compare = selected_option.compare;
			}

			try {
				this.loading = true;
				this.report_data = await reportSalesPerformance(report_query, refresh);
			} finally {
				this.loading = false;
			}
		},
		clearQuery() {
			this.loading = false;
		},
		handleTeamExpand(event) {},
		handleTeamCollapse(event) {},
	},
	async mounted() {
		await this.getReportData(this.query);
	},
};
</script>

<style lang="less" scoped>
:deep(.p-datatable-thead tr:first-child th) {
	border-bottom: 1px solid var(--gray-20);
}

:deep(.p-datatable-tbody) {
	overflow-y: auto;
}

:deep(td.shaded) {
	padding: 0 !important;
}

:deep(.color-cell) {
	padding: calc(0.75rem - 1px) 1rem;

	.sub-value {
		background-color: #fff;
		border-radius: 3px;
		display: inline-block;
		padding: 0 3px;
	}
}

:deep(td.column-align-right) {
	width: 14%;
}

:deep(tr.p-datatable-row-expansion > td) {
	padding: 0;

	thead {
		display: none;
	}
}

:deep(td.product-row-expander) {
	padding-left: 1.5rem !important;
}

:deep(td.user-row-name-cell) {
	padding-left: 5.5rem !important;
}
</style>
